import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { meetingService, Meeting, MeetingMemberStatus, MeetingPhoneListEntry } from '../services/meetingService';
import { getSiteConfig } from '../services/siteConfigService';
import { meetingCodeIsWorkshop2026, WORKSHOP_2026_PATH } from '../services/workshop2026Service';
import { useAuth } from '../context/AuthContext';
import JoinPreferencesModal, { JoinPreferences } from '../components/JoinPreferencesModal';
import styles from '../styles/meetingPage.module.css';

interface MeetingPageByCodeProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

type VisitorState = 'loading' | 'new_user' | 'existing_not_joined' | 'member';
type MeetingToolMode = 'meeting' | 'business' | 'treasury';

function formatMeetingTime(startTime: string | undefined): string {
  if (!startTime || !/^\d{1,2}:\d{2}$/.test(startTime)) return '';
  const [h, m] = startTime.split(':').map(Number);
  const hour = h % 12 || 12;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatTzAbbrev(tz: string | undefined): string {
  if (!tz) return '';
  const parts = tz.split('/');
  const last = parts[parts.length - 1];
  const known: Record<string, string> = {
    'Los_Angeles': 'PT', 'New_York': 'ET', 'Chicago': 'CT', 'Denver': 'MT',
    'Phoenix': 'MST', 'Anchorage': 'AKT', 'Honolulu': 'HST', 'London': 'GMT',
  };
  return known[last] || last.replace('_', ' ').slice(0, 3);
}

const MeetingPageByCode: React.FC<MeetingPageByCodeProps> = ({
  user,
  onLogout,
  onLogin,
  onCreateAccount,
}) => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [visitorState, setVisitorState] = useState<VisitorState>('loading');
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [savingToList, setSavingToList] = useState(false);
  const [joinPreferencesOpen, setJoinPreferencesOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [memberStatus, setMemberStatus] = useState<MeetingMemberStatus | null>(null);
  const [phoneList, setPhoneList] = useState<MeetingPhoneListEntry[]>([]);
  const [loadingPhoneList, setLoadingPhoneList] = useState(false);
  const [phoneListError, setPhoneListError] = useState<string | null>(null);
  const [requestVerificationLoading, setRequestVerificationLoading] = useState(false);
  const [activeMeetingMode, setActiveMeetingMode] = useState<MeetingToolMode>('meeting');
  /** Pass phrase for meetings with join_rule === 'meeting_key'. Pre-filled from ?key= URL param. */
  const [joinKey, setJoinKey] = useState(() => searchParams.get('key') ?? '');

  const effectiveUser = user || authUser;
  const joinRule = meeting?.join_rule ?? 'anyone';
  const needsKey = joinRule === 'meeting_key';
  const canAddSelf = !needsKey || joinKey.trim().length > 0;

  usePageTracking(`Meeting: ${code || 'unknown'}`);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    getSiteConfig().then((site) => {
      if (cancelled) return;
      if (meetingCodeIsWorkshop2026(code, site.workshop_2026_meeting_code)) {
        navigate(WORKSHOP_2026_PATH, { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  useEffect(() => {
    if (!code) return;
    const load = async () => {
      try {
        setError(null);
        const m = await meetingService.getMeetingByCode(code);
        setMeeting(m);
        if (!effectiveUser) {
          setVisitorState('new_user');
          return;
        }
        const [status, homeMeetings] = await Promise.all([
          meetingService.getMemberStatus(code),
          meetingService.getHomeMeetings(),
        ]);
        setVisitorState(status.is_member ? 'member' : 'existing_not_joined');
        setIsAdmin(status.is_admin);
        setMemberStatus(status);
        setIsSaved(homeMeetings.some((h) => h.meeting_code === (m?.meeting_code || '').toUpperCase()));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Meeting not found');
        setMeeting(null);
      }
    };
    load();
  }, [code, effectiveUser]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const mode = meeting?.phone_list_visibility_mode || 'off';
      const shouldLoad =
        visitorState === 'member' &&
        !!memberStatus &&
        memberStatus.is_member &&
        mode !== 'off' &&
        (mode === 'verified_only'
          ? memberStatus.is_verified || memberStatus.is_manager || memberStatus.is_admin
          : mode === 'managers_only'
            ? memberStatus.is_manager || memberStatus.is_admin
            : false);
      if (!shouldLoad) return;

      setLoadingPhoneList(true);
      setPhoneListError(null);
      try {
        const res = await meetingService.getPhoneList(meeting?.meeting_code || code || '');
        if (cancelled) return;
        setPhoneList(Array.isArray(res.phone_list) ? res.phone_list : []);
      } catch (err) {
        if (cancelled) return;
        setPhoneListError(err instanceof Error ? err.message : 'Failed to load phone list');
      } finally {
        if (cancelled) return;
        setLoadingPhoneList(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [visitorState, memberStatus, meeting?.phone_list_visibility_mode, meeting?.meeting_code, code]);

  const handleSaveToList = async () => {
    if (!code || !effectiveUser || savingToList) return;
    setSavingToList(true);
    try {
      await meetingService.saveToHome(code);
      setIsSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingToList(false);
    }
  };

  const handleRemoveFromList = async () => {
    if (!code || !effectiveUser || savingToList) return;
    setSavingToList(true);
    try {
      await meetingService.removeFromHome(code);
      setIsSaved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setSavingToList(false);
    }
  };

  const handleOpenJoinPreferences = () => {
    setJoinPreferencesOpen(true);
  };

  const handleJoinWithPreferences = async (preferences: JoinPreferences) => {
    if (!code || !effectiveUser || joining) return;
    setJoining(true);
    try {
      await meetingService.joinMeeting(code, {
        ...preferences,
        ...(needsKey && joinKey.trim() ? { join_key: joinKey.trim() } : {}),
      });
      setJoinPreferencesOpen(false);
      setVisitorState('member');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add you to the meeting');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinAsNewUser = () => {
    onCreateAccount ? onCreateAccount() : onLogin();
  };

  const programId = (meeting?.program_id || 'saa').toLowerCase();
  const programLabel = (meeting?.program_id || 'SAA').toUpperCase();
  const meetingFormatLabel = meeting
    ? meeting.format === 'telemeeting'
      ? 'Telemeeting'
      : meeting.format === 'hybrid'
        ? 'Hybrid'
        : meeting.format === 'in_person'
          ? 'In-Person'
          : 'Online'
    : '';
  const meetingBreadcrumbLabel = meeting
    ? `${(meeting.name || '').trim() || meetingFormatLabel} (${meeting.meeting_code})`
    : (code || 'Meeting');
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: programLabel, path: `/${programId}`, active: false },
    { label: 'Meetings', path: `/${programId}?tab=meetings`, active: false },
    { label: meetingBreadcrumbLabel, path: '', active: true },
  ];

  if (error) {
    return (
      <div className={styles.meetingPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.error}>
            <h2>Meeting not found</h2>
            <p>{error}</p>
            <a href={`/${programId}?tab=meetings`} className={styles.backLink}>
              ← {programLabel} meetings
            </a>
          </div>
        </main>
      </div>
    );
  }

  if (!meeting && visitorState === 'loading') {
    return (
      <div className={styles.meetingPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.loading}>Loading meeting...</div>
        </main>
      </div>
    );
  }

  if (!meeting) return null;

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/meetings/${meeting.meeting_code}`;
  const showLocationPublic = meeting.link_visibility === 'everyone';
  const dayLabel = meeting.schedule_day || (meeting.workshop_first_session_date && (() => {
    try {
      const d = new Date(meeting.workshop_first_session_date + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'long' });
    } catch {
      return '';
    }
  })());
  const timeStr = formatMeetingTime(meeting.schedule_start_time);
  const tzStr = formatTzAbbrev(meeting.timezone);
  const timeAndTz = [timeStr, tzStr].filter(Boolean).join(' ');
  const locationLabel = meeting.location?.trim() || (meeting.format === 'telemeeting' ? 'Telemeeting' : meeting.format === 'hybrid' ? 'Hybrid' : meeting.format === 'in_person' ? 'In-person' : 'Online');
  const isMember = visitorState === 'member';

  const serviceRoles = memberStatus?.service_roles || [];
  const isPrivilegedForTools = !!memberStatus && (memberStatus.is_admin || memberStatus.is_manager);
  const canUseMeetingMode =
    isPrivilegedForTools || serviceRoles.includes('Secretary') || serviceRoles.includes('Host');
  const canUseBusinessMeetingMode =
    isPrivilegedForTools || serviceRoles.includes('Secretary') || serviceRoles.includes('Scribe');
  const canUseTreasuryMode = isPrivilegedForTools || serviceRoles.includes('Treasurer');

  const availableMeetingModes: Array<{ id: MeetingToolMode; label: string; can: boolean }> = [];
  if (canUseMeetingMode) availableMeetingModes.push({ id: 'meeting', label: 'Meeting Mode', can: true });
  if (canUseBusinessMeetingMode) {
    availableMeetingModes.push({ id: 'business', label: 'Business Meeting Mode', can: true });
  }
  if (canUseTreasuryMode) availableMeetingModes.push({ id: 'treasury', label: 'Treasury (phase 1)', can: true });

  const effectiveActiveMeetingMode: MeetingToolMode =
    availableMeetingModes.some((m) => m.id === activeMeetingMode)
      ? activeMeetingMode
      : availableMeetingModes[0]?.id || 'meeting';

  const getMemberRoleLabel = (status: MeetingMemberStatus | null): string | null => {
    if (!status || !status.is_member) return null;
    if (status.is_admin) return 'Admin';
    if (status.is_manager) return 'Manager';
    if (status.service_roles && status.service_roles.length > 0) return `Service role: ${status.service_roles[0]}`;
    if (status.is_verified) return 'Verified';
    return 'Participant';
  };
  const roleLabel = getMemberRoleLabel(memberStatus);
  const phoneListMode = meeting?.phone_list_visibility_mode || 'off';
  const canViewPhoneList =
    !!memberStatus &&
    memberStatus.is_member &&
    phoneListMode !== 'off' &&
    (phoneListMode === 'verified_only'
      ? memberStatus.is_verified || memberStatus.is_manager || memberStatus.is_admin
      : phoneListMode === 'managers_only'
        ? memberStatus.is_manager || memberStatus.is_admin
        : false);

  const handleRequestVerification = async () => {
    if (!code || !effectiveUser || requestVerificationLoading) return;
    setRequestVerificationLoading(true);
    setError(null);
    try {
      const status = await meetingService.requestMeetingVerification(code);
      setMemberStatus(status);
      setIsAdmin(status.is_admin);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request verification');
    } finally {
      setRequestVerificationLoading(false);
    }
  };

  const handleCopyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {});
  };

  return (
    <div className={styles.meetingPage}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.mainContent}>
        {/* #1 Core meeting info — everyone */}
        <div className={isMember ? styles.coreBlockCompact : styles.coreBlock}>
          <a href={`/${programId}?tab=meetings`} className={styles.backToMeetingsLink}>
            ← {programLabel} meetings
          </a>
          <h1 className={styles.meetingName}>{meeting.name}</h1>
          {isMember && roleLabel && (
            <p className={styles.memberRoleBadge}>{roleLabel}</p>
          )}
          <p className={styles.meetingMeta}>
            {meeting.kind === 'workshop' ? 'Workshop' : 'Meeting'} · {meeting.program_id?.toUpperCase()}
            {isMember && (dayLabel || timeAndTz) && ` · ${[dayLabel, timeAndTz].filter(Boolean).join(' · ')}`}
            {isMember && showLocationPublic && (meeting.location || meeting.format) && ` · ${locationLabel}`}
          </p>
          {!isMember && (dayLabel || timeAndTz) && (
            <p className={styles.meetingSchedule}>
              {[dayLabel, timeAndTz].filter(Boolean).join(' · ')}
            </p>
          )}
          {!isMember && showLocationPublic && (meeting.location || meeting.format) && (
            <p className={styles.meetingLocation}>{locationLabel}</p>
          )}
          {meeting.description?.trim() && (
            <p className={styles.meetingDescription}>{meeting.description}</p>
          )}
        </div>

        {/* #2 Join — not logged in */}
        {visitorState === 'new_user' && (
          <div className={styles.joinBlock}>
            <p className={styles.joinIntro}>
              Want to connect with this meeting? Sign in or create an account to add this meeting to your My Meetings list and get the join link, member list, and meeting updates.
            </p>
            <button type="button" onClick={handleJoinAsNewUser} className={styles.primaryButton}>
              Sign in or create account
            </button>
            <p className={styles.verifyHint}>
              After creating an account, check your email to verify. Once verified, return to this page and click &quot;Add me to this meeting&quot;.
            </p>
          </div>
        )}

        {/* #2 Join — logged in, not member */}
        {effectiveUser && visitorState !== 'member' && visitorState !== 'new_user' && (
          <div className={styles.joinBlock}>
            <p className={styles.saveToListLine}>
              {isSaved ? (
                <button
                  type="button"
                  onClick={handleRemoveFromList}
                  disabled={savingToList}
                  className={styles.saveToListLink}
                >
                  {savingToList ? '...' : 'Remove from My Meetings'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSaveToList}
                  disabled={savingToList}
                  className={styles.saveToListLink}
                >
                  {savingToList ? 'Saving...' : 'Add to My Meetings'}
                </button>
              )}
            </p>
            {joinRule === 'approval_required' && (
              <p className={styles.joinMessage}>
                This meeting requires approval to join. Request access from a meeting contact.
              </p>
            )}
            {joinRule === 'invite_only' && (
              <p className={styles.joinMessage}>
                This meeting is invite-only. Contact a meeting contact for an invitation.
              </p>
            )}
            {(joinRule === 'anyone' || joinRule === 'meeting_key') && (
              <>
                <p className={styles.joinIntro}>
                  Add this meeting to your My Meetings list to get the link and member list.
                </p>
                {needsKey && (
                  <>
                    <label htmlFor="meeting-join-key" className={styles.keyLabel}>
                      Pass phrase
                    </label>
                    <input
                      id="meeting-join-key"
                      type="text"
                      value={joinKey}
                      onChange={(e) => setJoinKey(e.target.value)}
                      placeholder="Enter pass phrase, or use the join link from the meeting"
                      className={styles.keyInput}
                      autoComplete="off"
                    />
                    <p className={styles.keyHint}>
                      If someone shared a join link with you, open that link instead of typing the phrase.
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={handleOpenJoinPreferences}
                  disabled={joining || !canAddSelf}
                  className={styles.primaryButton}
                >
                  {joining ? 'Adding…' : 'Add me to this meeting'}
                </button>
              </>
            )}
          </div>
        )}

        {/* #2 Join — member */}
        {visitorState === 'member' && (
          <div className={styles.joinBlock}>
            <p className={styles.memberStatus}>You&apos;re in this meeting.</p>
            {memberStatus && !memberStatus.is_verified && (
              <div className={styles.verificationBlock}>
                <p className={styles.memberStatusSub}>
                  {memberStatus.verification_requested_at
                    ? 'Verification requested. You will be able to view the phone list once approved.'
                    : 'Request verification to access the meeting phone list.'}
                </p>
                {!memberStatus.verification_requested_at && (
                  <button
                    type="button"
                    onClick={handleRequestVerification}
                    disabled={requestVerificationLoading}
                    className={styles.primaryButton}
                  >
                    {requestVerificationLoading ? 'Requesting…' : 'Request verification'}
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleRemoveFromList}
              disabled={savingToList}
              className={styles.secondaryButton}
            >
              {savingToList ? '...' : 'Remove from My Meetings'}
            </button>
          </div>
        )}

        <JoinPreferencesModal
          isOpen={joinPreferencesOpen}
          onClose={() => setJoinPreferencesOpen(false)}
          onSubmit={handleJoinWithPreferences}
          isSubmitting={joining}
          meetingName={meeting?.name}
        />

        {/* #3 Member area — members only; #4 admin link to settings */}
        {visitorState === 'member' && (
          <div className={styles.memberArea}>
            <h2 className={styles.memberAreaHeading}>Member area</h2>

            <div className={styles.shareCard}>
              <span className={styles.shareLabel}>Share this meeting</span>
              <div className={styles.shareRow}>
                <code className={styles.shareUrl}>{shareUrl}</code>
                <button type="button" onClick={handleCopyShareUrl} className={styles.copyButton}>
                  Copy
                </button>
              </div>
            </div>

            {isAdmin && (
              <a href={`/meetings/${meeting.meeting_code}/settings`} className={styles.operationsLink}>
                Meeting settings &amp; operations →
              </a>
            )}

            {canViewPhoneList && (
              <section className={styles.memberCard} aria-labelledby="phone-list-heading">
                <h2 id="phone-list-heading" className={styles.memberCardTitle}>Phone list</h2>
                {loadingPhoneList ? (
                  <p className={styles.memberCardPlaceholder}>Loading…</p>
                ) : phoneListError ? (
                  <p className={styles.memberCardPlaceholder}>{phoneListError}</p>
                ) : phoneList.length === 0 ? (
                  <p className={styles.memberCardPlaceholder}>No phone list entries available.</p>
                ) : (
                  <ul className={styles.phoneList}>
                    {phoneList.map((p) => (
                      <li key={p.user_id} className={styles.phoneListRow}>
                        <span className={styles.phoneListName}>{p.display_name}</span>
                        <span className={styles.phoneListPhone}>{p.phone}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {availableMeetingModes.length > 0 ? (
              <>
                <div className={styles.modeTabs} role="tablist" aria-label="Meeting tools">
                  {availableMeetingModes.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      role="tab"
                      aria-selected={m.id === effectiveActiveMeetingMode}
                      className={m.id === effectiveActiveMeetingMode ? styles.modeTabActive : styles.modeTab}
                      onClick={() => setActiveMeetingMode(m.id)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {effectiveActiveMeetingMode === 'meeting' && (
                  <>
                    <section className={styles.memberCard} aria-labelledby="meeting-script-heading">
                      <h2 id="meeting-script-heading" className={styles.memberCardTitle}>Meeting Mode: script &amp; guidance</h2>
                      <p className={styles.memberCardPlaceholder}>Leader&apos;s script (private guidance) — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="meeting-readings-heading">
                      <h2 id="meeting-readings-heading" className={styles.memberCardTitle}>Meeting Mode: readings display</h2>
                      <p className={styles.memberCardPlaceholder}>Display of readings + “Next Reading” flow — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="meeting-flow-heading">
                      <h2 id="meeting-flow-heading" className={styles.memberCardTitle}>Meeting Mode: announcements &amp; flow</h2>
                      <p className={styles.memberCardPlaceholder}>Announcements, QR donation info, and presentation flow — coming soon.</p>
                    </section>
                  </>
                )}

                {effectiveActiveMeetingMode === 'business' && (
                  <>
                    <section className={styles.memberCard} aria-labelledby="business-notes-heading">
                      <h2 id="business-notes-heading" className={styles.memberCardTitle}>Business Meeting Mode: structured notes</h2>
                      <p className={styles.memberCardPlaceholder}>Agenda, decisions, and business meeting notes — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="business-reports-heading">
                      <h2 id="business-reports-heading" className={styles.memberCardTitle}>Business Meeting Mode: service reports</h2>
                      <p className={styles.memberCardPlaceholder}>Service reports, old/new business, and carried-forward items — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="business-treasurer-heading">
                      <h2 id="business-treasurer-heading" className={styles.memberCardTitle}>Business Meeting Mode: treasurer report insertion</h2>
                      <p className={styles.memberCardPlaceholder}>Treasurer report insertion point — coming soon.</p>
                    </section>
                  </>
                )}

                {effectiveActiveMeetingMode === 'treasury' && (
                  <>
                    <section className={styles.memberCard} aria-labelledby="treasury-donate-heading">
                      <h2 id="treasury-donate-heading" className={styles.memberCardTitle}>Treasury (phase 1): donation method</h2>
                      <p className={styles.memberCardPlaceholder}>Donation method display + donation link/QR code — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="treasury-ledger-heading">
                      <h2 id="treasury-ledger-heading" className={styles.memberCardTitle}>Treasury (phase 1): ledger entries</h2>
                      <p className={styles.memberCardPlaceholder}>Ledger entry recording + treasurer report generation — coming soon.</p>
                    </section>
                    <section className={styles.memberCard} aria-labelledby="treasury-print-heading">
                      <h2 id="treasury-print-heading" className={styles.memberCardTitle}>Treasury (phase 1): printable summary</h2>
                      <p className={styles.memberCardPlaceholder}>
                        Printable treasurer summaries and “as of” dates — coming soon.
                      </p>
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        disabled
                        style={{ marginTop: '0.75rem' }}
                      >
                        Print treasurer summary (coming soon)
                      </button>
                    </section>
                  </>
                )}
              </>
            ) : (
              <section className={styles.memberCard} aria-labelledby="tools-unavailable-heading">
                <h2 id="tools-unavailable-heading" className={styles.memberCardTitle}>Meeting tools</h2>
                <p className={styles.memberCardPlaceholder}>
                  This meeting’s tools are available to assigned roles (e.g., Secretary, Host, Scribe, Treasurer). You can still join and participate.
                </p>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default MeetingPageByCode;
